import {
  Alert,
  Icons,
  InputChipV2,
  LayoutCard,
  MetadataChipV2,
  MultiAutocompleteV2,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

import { CAST_AI } from '@cast/constants';

import { useWoopWorkloadFilters } from '@cast/split-me/features/workload-optimization/hooks';

import { DEFAULT_EXCLUDED_NAMESPACES } from '../../_constants';
import { useAutoOptimization } from '../../_hooks/useAutoOptimization';
import { WizardScreenLayout } from '../wizard/WizardScreenLayout';
import { IncludedLabel } from './_components/IncludedLabel';

export const ExclusionsScreen = () => {
  const { state, onSetExcludedNamespaces } = useAutoOptimization();
  const { data } = useWoopWorkloadFilters();

  const allNamespaces = (data?.namespaces ?? []).filter(
    (ns) => !(DEFAULT_EXCLUDED_NAMESPACES as readonly string[]).includes(ns)
  );

  return (
    <WizardScreenLayout
      title="What should we protect?"
      description={`Select the namespaces ${CAST_AI} should skip. Protected namespaces won't be rightsized or consolidated — the two operations that restart pods. To also protect against node removal, add the label`}
    >
      <Stack gap={16}>
        <Alert color="info" size="sm" icon={<Icons.Info weight="light" />}>
          <Typography variant="P12R">
            System namespaces are always protected under a read-only{' '}
            <MetadataChipV2
              label="system-no-touch"
              size="xs"
              variant="subtle"
            />{' '}
            policy so the exclusion stays auditable.
          </Typography>
        </Alert>

        <LayoutCard>
          <Stack gap={12}>
            <Typography
              variant="A1"
              sx={{ color: tokens.theme.fg.structural.primary }}
            >
              Protect namespaces
            </Typography>
            <MultiAutocompleteV2
              items={allNamespaces}
              value={state.excludedNamespaces}
              onChange={({ values }) => onSetExcludedNamespaces(values)}
              placeholder="Search namespaces..."
              sx={{ width: '100%' }}
            />
            {state.excludedNamespaces.length > 0 && (
              <Stack direction="row" gap={6} flexWrap="wrap">
                {state.excludedNamespaces.map((ns) => (
                  <InputChipV2
                    key={ns}
                    label={ns}
                    onDelete={() =>
                      onSetExcludedNamespaces(
                        state.excludedNamespaces.filter((n) => n !== ns)
                      )
                    }
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </LayoutCard>
        <IncludedLabel text="Protection applies to workload rightsizing and bin-packing only. Upscaling and rebalancing apply to all namespaces regardless." />
      </Stack>
    </WizardScreenLayout>
  );
};
